import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';

/* Smooth scroll progress bar at top of page */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: 'linear-gradient(90deg, #3E7562, #549E84)',
        transformOrigin: '0%',
        scaleX,
        zIndex: 9999,
      }}
    />
  );
}

/* Image that zooms slightly on scroll */
export function ParallaxImage({ src, alt, height = 500, speed = 0.2 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [speed * -100, speed * 100]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1, 1.05]);

  return (
    <div ref={ref} style={{ overflow: 'hidden', height, position: 'relative' }}>
      <motion.img
        src={src}
        alt={alt}
        style={{
          y,
          scale,
          width: '100%',
          height: '130%',
          objectFit: 'cover',
          position: 'absolute',
          top: '-15%',
        }}
      />
    </div>
  );
}

/* Image that reveals on scroll with a sliding mask */
export function RevealImage({ src, alt, style = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <div ref={ref} style={{ overflow: 'hidden', position: 'relative', ...style }}>
      <motion.div
        initial={{ scaleY: 1 }}
        animate={isInView ? { scaleY: 0 } : {}}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'absolute',
          inset: 0,
          background: '#111112',
          transformOrigin: 'top',
          zIndex: 2,
        }}
      />
      <motion.img
        src={src}
        alt={alt}
        initial={{ scale: 1.3 }}
        animate={isInView ? { scale: 1 } : {}}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
}

/* Text that reveals word by word on scroll */
export function WordReveal({ text, className = '', style = {} }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const words = text.split(' ');

  return (
    <span ref={ref} className={className} style={{ display: 'inline', ...style }}>
      {words.map((word, i) => (
        <span key={i} style={{ display: 'inline-block', overflow: 'hidden', marginRight: '0.3em' }}>
          <motion.span
            style={{ display: 'inline-block' }}
            initial={{ y: '110%', opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{
              duration: 0.5,
              delay: i * 0.06,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

/* Counter that animates numbers when in view */
export function AnimatedCounter({ value, suffix = '', prefix = '', duration = 2 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
    >
      {prefix}
      <motion.span
        initial={{ opacity: 1 }}
        onViewportEnter={(entry) => {
          const el = entry?.target;
          if (!el || !isInView) return;
          let start = null;
          const step = (ts) => {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - progress, 4);
            const current = Math.floor(numericValue * eased);
            el.textContent = current.toLocaleString() + suffix;
            if (progress < 1) requestAnimationFrame(step);
            else el.textContent = value + suffix;
          };
          requestAnimationFrame(step);
        }}
      >
        0{suffix}
      </motion.span>
    </motion.span>
  );
}

/* Horizontal scroll section */
export function HorizontalScroll({ children }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  });
  const x = useTransform(scrollYProgress, [0, 1], ['0%', '-60%']);

  return (
    <div ref={ref} style={{ height: '300vh', position: 'relative' }}>
      <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <motion.div style={{ x, display: 'flex', gap: 24, paddingLeft: 48 }}>
          {children}
        </motion.div>
      </div>
    </div>
  );
}

/* Image with hover zoom + overlay */
export function HoverImage({ src, alt, overlay = '', style = {} }) {
  return (
    <motion.div
      style={{
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        ...style,
      }}
      whileHover="hovered"
      initial="idle"
    >
      <motion.img
        src={src}
        alt={alt}
        variants={{
          idle: { scale: 1 },
          hovered: { scale: 1.08 },
        }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      <motion.div
        variants={{
          idle: { opacity: 0 },
          hovered: { opacity: 1 },
        }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, transparent 40%, rgba(0,30,23,0.85) 100%)',
          display: 'flex',
          alignItems: 'flex-end',
          padding: 24,
        }}
      >
        <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 600 }}>{overlay}</span>
      </motion.div>
    </motion.div>
  );
}

/* Magnetic button effect */
export function MagneticButton({ children, className = '', style = {}, ...props }) {
  const ref = useRef(null);

  const handleMouse = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    el.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
  };

  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = 'translate(0, 0)';
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      className={className}
      style={{ transition: 'transform 0.3s ease', display: 'inline-block', ...style }}
      {...props}
    >
      {children}
    </div>
  );
}
